import React from 'react'
import { motion } from 'framer-motion'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PauseIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

interface MonitoringStatusBadgeProps {
  status: 'active' | 'inactive' | 'error'
  nextRun?: string | null
  className?: string
}

const MonitoringStatusBadge: React.FC<MonitoringStatusBadgeProps> = ({
  status,
  nextRun,
  className = ''
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'active':
        return {
          icon: CheckCircleIcon,
          text: 'Active',
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          iconColor: 'text-green-600',
          dotColor: 'bg-green-400'
        }
      case 'inactive':
        return {
          icon: PauseIcon,
          text: 'Inactive',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-600',
          dotColor: 'bg-yellow-400'
        }
      case 'error':
        return {
          icon: ExclamationTriangleIcon,
          text: 'Error',
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          iconColor: 'text-red-600',
          dotColor: 'bg-red-400'
        }
      default:
        return {
          icon: ClockIcon,
          text: 'Unknown',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          iconColor: 'text-gray-600',
          dotColor: 'bg-gray-400'
        }
    }
  }

  const config = getStatusConfig()
  const Icon = config.icon

  const formatNextRun = (dateString: string | null) => {
    if (!dateString) return null

    // Parse the date string correctly - handle both formats
    let date
    if (dateString.includes('T')) {
      // ISO format: 2025-09-26T14:59:01
      date = new Date(dateString)
    } else {
      // Our format: 2025-09-26 14:59:01
      date = new Date(dateString.replace(' ', 'T'))
    }

    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffMinutes = Math.floor(diffMs / 60000)

    if (diffMinutes < 0) return 'Running...'
    if (diffMinutes < 1) return '<1m'
    if (diffMinutes < 60) return `${diffMinutes}m`

    const diffHours = Math.floor(diffMinutes / 60)
    const remainingMinutes = diffMinutes % 60

    if (diffHours < 24) {
      return remainingMinutes > 0 ? `${diffHours}h${remainingMinutes}m` : `${diffHours}h`
    }

    return date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`relative inline-flex items-center px-3 py-2 rounded-full ${config.bgColor} ${className}`}
      title={`Monitoring: ${config.text}${nextRun ? ` • Next: ${formatNextRun(nextRun)}` : ''}`}
    >
      {/* Animated status dot */}
      <div className="flex items-center">
        <div className="relative mr-2">
          <div className={`w-2 h-2 rounded-full ${config.dotColor}`}>
            {status === 'active' && (
              <div className={`w-2 h-2 rounded-full ${config.dotColor} animate-ping absolute`}></div>
            )}
          </div>
        </div>

        <Icon className={`h-4 w-4 ${config.iconColor} mr-1`} />

        <span className={`text-sm font-medium ${config.textColor}`}>
          {config.text}
        </span>

        {nextRun && status === 'active' && (
          <span className={`ml-2 text-xs ${config.textColor} opacity-75 font-mono`}>
            {formatNextRun(nextRun)}
          </span>
        )}
      </div>
    </motion.div>
  )
}

export default MonitoringStatusBadge